import { Grow } from '@mui/material';
import Grid2 from '@mui/material/Unstable_Grid2';
import { differenceWith, isEqual } from 'lodash';
import { useState } from 'react';

import Button, {
  ButtonSize,
  ButtonVariant,
} from '@/components/BaseLibrary/Button/Button';
import BlogCard from '@/components/BlogComponents/shared/BlogCard/BlogCard';
import { IconName } from '@/components/Icon/Icon';
import GridContainer2 from '@/components/Layout/GridContainer2/GridContainer2';
import { SpacingContainer } from '@/components/SpacingContainer/SpacingContainer';
import type { BlogPageType } from '@/contentful/api/api.types';
import { DYNAMIC_GRID_ARTICLE_LIMIT } from '@/contentful/constants';
import useGlobalStyles from '@/styles/global.styles';
import { toCamelCase } from '@/utils/utils';

import useStyles from './BlogArticle3Up.styles';

const BASE_DELAY = 300;
const DELAY_MULTIPLIER = 200;

const dedupe = (loadedData: BlogPageType[], initialItems: BlogPageType[]) => {
  if (loadedData.length === 0) return [];
  return differenceWith(loadedData, initialItems, isEqual);
};

function getAdditionalArticles(
  blogCategory: string | undefined,
  initialArticles: BlogPageType[],
  isCurated: boolean
): BlogPageType[] {
  let loadedData: BlogPageType[] = [];
  let additionalArticles: BlogPageType[] = [];

  // Don't want to fetch dynamic articles if curated
  if (isCurated) {
    return additionalArticles;
  }

  try {
    if (blogCategory) {
      loadedData = require(
        `../../../public/blog/${toCamelCase(blogCategory)}.json`
      );
    } else {
      loadedData = require(`../../../public/blog/allBlogs.json`);
    }

    additionalArticles = dedupe(loadedData, initialArticles);
  } catch {
    console.error('Could not find file for blog category or blog hub', {
      blogCategory,
    });
  }

  return additionalArticles;
}

const BlogArticle3Up = ({
  blogCategory,
  heading,
  initialArticles,
  isCurated = false,
}: {
  initialArticles: BlogPageType[];
  blogCategory?: string;
  heading?: string;
  isCurated?: boolean;
}) => {
  const [paginationIdx, setPaginationIdx] = useState(-1);
  const [mountedItemsIdx, setMountedItemsIdx] = useState(5);
  const { classes: globalClasses } = useGlobalStyles({ fixed: true });
  const { classes, cx } = useStyles();

  const additionalArticles = getAdditionalArticles(
    blogCategory,
    initialArticles,
    isCurated
  );

  const mountedItems = additionalArticles.slice(0, mountedItemsIdx + 1);
  const maxPaginationIdx = additionalArticles.length - 1;
  const showButton =
    initialArticles.length >= DYNAMIC_GRID_ARTICLE_LIMIT &&
    additionalArticles.length > 0 &&
    maxPaginationIdx - paginationIdx > 0;

  const clickHandler = () => {
    const futurePaginationIdx = Math.min(
      paginationIdx + DYNAMIC_GRID_ARTICLE_LIMIT,
      maxPaginationIdx
    );
    const futureMountedItemsIdx = Math.min(
      mountedItemsIdx + DYNAMIC_GRID_ARTICLE_LIMIT,
      maxPaginationIdx
    );

    setPaginationIdx(futurePaginationIdx);
    setMountedItemsIdx(futureMountedItemsIdx);
  };

  return (
    <SpacingContainer
      isFeatured
      className={classes.spacingContainer}
      data-testid='blogArticle3Up'
    >
      <GridContainer2 fixed>
        <Grid2 xs={12}>
          {heading && <h2 className={globalClasses.h2}>{heading}</h2>}
        </Grid2>
      </GridContainer2>
      <GridContainer2 fixed>
        {initialArticles.map(
          (
            {
              blogTitle,
              path,
              readTimeMinutes,
              category,
              categoryPath,
              publicationDate,
              heroImage,
            }: BlogPageType,
            idx: number
          ) => (
            <Grid2
              xs={12}
              sm={6}
              md={4}
              key={`${blogTitle}-${
                // biome-ignore lint/suspicious/noArrayIndexKey: Automated migration to Biome
                idx
              }`}
              className={classes.cardWrapper}
              data-testid='blog-card-visible'
            >
              <BlogCard
                title={blogTitle}
                url={path}
                readTime={readTimeMinutes}
                category={category}
                date={publicationDate}
                image={heroImage}
                categoryPath={categoryPath}
              />
            </Grid2>
          )
        )}
        {mountedItems.map(
          (
            {
              blogTitle,
              path,
              readTimeMinutes,
              category,
              categoryPath,
              publicationDate,
              heroImage,
            }: BlogPageType,
            idx: number
          ) => (
            <Grow
              in={idx <= paginationIdx}
              key={`${blogTitle}-${
                // biome-ignore lint/suspicious/noArrayIndexKey: Automated migration to Biome
                idx
              }`}
              timeout={
                BASE_DELAY +
                ((idx % DYNAMIC_GRID_ARTICLE_LIMIT) + 1) * DELAY_MULTIPLIER
              }
              data-testid={`blog-card-${
                idx <= paginationIdx ? 'visible' : 'hidden'
              }`}
              className={cx(classes.cardWrapper, {
                [classes.invisibleCard]: idx > paginationIdx,
              })}
            >
              <Grid2 xs={12} sm={6} md={4}>
                <BlogCard
                  title={blogTitle}
                  url={path}
                  readTime={readTimeMinutes}
                  category={category}
                  date={publicationDate}
                  image={heroImage}
                  categoryPath={categoryPath}
                />
              </Grid2>
            </Grow>
          )
        )}
      </GridContainer2>
      <GridContainer2 fixed>
        {showButton && (
          <Grid2 xs={12}>
            <div className={classes.buttonWrapper}>
              <Button
                onClick={clickHandler}
                label='Load more'
                variant={ButtonVariant.SECONDARY}
                iconName={IconName.CHEVRON_RIGHT}
                size={{ xs: ButtonSize.SM, md: ButtonSize.MD }}
                className={classes.buttonOverride}
              />
            </div>
          </Grid2>
        )}
      </GridContainer2>
    </SpacingContainer>
  );
};

export default BlogArticle3Up;
